import React, { ErrorInfo, ReactNode, useCallback } from 'react';
import { FallbackProps, ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

import { captureErrorWithContext } from '../../utils/error-capture.utils';

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  context?: string;
  onError?: (error: Error, errorInfo: { componentStack: ErrorInfo['componentStack'] }) => void;
}

export function ErrorBoundary({ children, fallback, onError, context }: ErrorBoundaryProps) {
  const handleError = useCallback(
    (error: Error, errorInfo: ErrorInfo) => {
      // Create a new error instead of mutating the message property
      // which might be read-only in some error types
      const enhancedError = new Error(`[Crash]: ${error.message}`);

      enhancedError.name = error.name;
      enhancedError.stack = error.stack;

      captureErrorWithContext(
        enhancedError,
        {
          context: context || 'ErrorBoundary',
        },
        0
      );

      if (onError) {
        onError(enhancedError, {
          componentStack: errorInfo.componentStack || '',
        });
      }

      console.error(error);
    },
    [onError]
  );

  const FallbackComponent: React.FC<FallbackProps> = ({
    error,
    resetErrorBoundary,
  }: FallbackProps) => {
    if (!fallback) return null;

    // return (
    //   <div>
    //     {fallback ?? <div>Something went wrong.</div>}
    //     <button onClick={resetErrorBoundary}>Try again</button>
    //   </div>
    // );

    return fallback;
  };

  return (
    <ReactErrorBoundary FallbackComponent={FallbackComponent} onError={handleError}>
      {children}
    </ReactErrorBoundary>
  );
}
